<template>
  <div>
    <div class="flex flex-col gap-3 mb-4 sm:mb-6 sm:flex-row sm:justify-between sm:items-center">
      <h2 class="text-xl sm:text-2xl font-bold text-gray-900">Mitglieder verwalten</h2>
      <button
        @click="openCreateModal"
        class="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
      >
        Neues Mitglied
      </button>
    </div>

    <div v-if="loading" class="text-center py-8">Laden...</div>
    <div v-else-if="error" class="rounded-md bg-red-50 p-4 mb-4">
      <p class="text-sm text-red-800">{{ error }}</p>
    </div>
    <div v-else>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Foto</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vorname</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nachname</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farbe</th>
              <th class="px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trikotnummer</th>
              <th class="sticky right-0 bg-gray-50 px-3 py-3 sm:px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="member in members" :key="member.id">
              <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ member.id }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                <img
                  :src="memberPhotoUrl(member.id, member.photoVersion)"
                  :alt="member.photoVersion ? `Foto von ${member.firstName}` : 'Kein Foto (Logo)'"
                  class="h-9 w-9 rounded-full object-cover ring-1 ring-gray-300"
                  :class="{ 'opacity-50': !member.photoVersion }"
                />
              </td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ member.firstName }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ member.lastName }}</td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                <span
                  class="inline-block w-6 h-6 rounded-full border border-gray-300"
                  :style="{ backgroundColor: member.color }"
                ></span>
              </td>
              <td class="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-900">{{ member.shirtNumber }}</td>
              <td class="sticky right-0 bg-white px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  @click="openEditModal(member)"
                  class="text-indigo-600 hover:text-indigo-900 mr-3 sm:mr-4"
                >
                  Bearbeiten
                </button>
                <button
                  @click="confirmDelete(member)"
                  class="text-red-600 hover:text-red-900"
                >
                  Löschen
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed z-10 inset-0 overflow-y-auto" @click.self="closeModal">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form @submit.prevent="saveMember">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                {{ editingMember ? 'Mitglied bearbeiten' : 'Neues Mitglied' }}
              </h3>
              <div v-if="formError" class="rounded-md bg-red-50 p-3 mb-4">
                <p class="text-sm text-red-800">{{ formError }}</p>
              </div>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Vorname</label>
                  <input
                    v-model="formData.firstName"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Nachname</label>
                  <input
                    v-model="formData.lastName"
                    type="text"
                    required
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Farbe</label>
                  <input
                    v-model="formData.color"
                    type="color"
                    class="mt-1 block w-full h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <span class="block text-sm font-medium text-gray-700">Foto</span>
                  <div class="mt-1 flex items-center gap-4">
                    <img :src="photoPreview" alt="Vorschau" class="h-20 w-20 flex-none rounded-full object-cover ring-1 ring-gray-300" />
                    <div class="flex flex-col items-start gap-2">
                      <label class="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        {{ hasPhoto ? 'Anderes Foto wählen' : 'Foto wählen' }}
                        <input type="file" accept="image/jpeg,image/png,image/webp" class="sr-only" @change="onPhotoSelected" />
                      </label>
                      <button v-if="hasPhoto" type="button" @click="removePhoto" class="text-sm text-red-600 hover:text-red-900">
                        Foto entfernen
                      </button>
                      <span v-else class="text-xs text-gray-500">Ohne Foto wird das Logo angezeigt.</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Trikotnummer</label>
                  <input
                    v-model.number="formData.shirtNumber"
                    type="number"
                    required
                    min="0"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-black focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Speichern
              </button>
              <button
                type="button"
                @click="closeModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import adminService, { type AdminMember } from '@/services/AdminService'
import { memberPhotoUrl, resizeImage } from '@/utils/photo'

const members = ref<AdminMember[]>([])
const loading = ref(false)
const error = ref('')
const formError = ref('')
const showModal = ref(false)
const editingMember = ref<AdminMember | null>(null)
const formData = ref({
  firstName: '',
  lastName: '',
  color: '#000000',
  shirtNumber: 0
})

// Foto-Änderungen im Dialog: neues Bild (verkleinert) oder Entfernen, gespeichert erst mit "Speichern"
const pendingPhoto = ref<Blob | null>(null)
const pendingPreviewUrl = ref<string | null>(null)
const photoRemoved = ref(false)

const resetPhotoState = () => {
  if (pendingPreviewUrl.value) URL.revokeObjectURL(pendingPreviewUrl.value)
  pendingPhoto.value = null
  pendingPreviewUrl.value = null
  photoRemoved.value = false
}

const hasPhoto = computed(() =>
  !!pendingPhoto.value || (!photoRemoved.value && !!editingMember.value?.photoVersion)
)

const photoPreview = computed(() => {
  if (pendingPreviewUrl.value) return pendingPreviewUrl.value
  const member = editingMember.value
  return memberPhotoUrl(member?.id ?? 0, photoRemoved.value ? null : member?.photoVersion)
})

const onPhotoSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  formError.value = ''
  try {
    const blob = await resizeImage(file)
    resetPhotoState()
    pendingPhoto.value = blob
    pendingPreviewUrl.value = URL.createObjectURL(blob)
  } catch (err: any) {
    formError.value = err.message || 'Das Bild konnte nicht gelesen werden'
  }
}

const removePhoto = () => {
  resetPhotoState()
  photoRemoved.value = true
}

onUnmounted(resetPhotoState)

const loadMembers = async () => {
  loading.value = true
  error.value = ''
  try {
    members.value = await adminService.getMembers()
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Fehler beim Laden der Mitglieder'
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  resetPhotoState()
  editingMember.value = null
  formData.value = {
    firstName: '',
    lastName: '',
    color: '#000000',
    shirtNumber: 0
  }
  showModal.value = true
}

const openEditModal = (member: AdminMember) => {
  resetPhotoState()
  editingMember.value = member
  formData.value = {
    firstName: member.firstName,
    lastName: member.lastName,
    color: member.color,
    shirtNumber: member.shirtNumber
  }
  showModal.value = true
}

const closeModal = () => {
  formError.value = ''
  showModal.value = false
  editingMember.value = null
  resetPhotoState()
}

const saveMember = async () => {
  formError.value = ''
  try {
    let memberId: number
    if (editingMember.value) {
      memberId = editingMember.value.id
      await adminService.updateMember(memberId, formData.value)
    } else {
      const created = await adminService.createMember(formData.value)
      memberId = created.id
      // Schlägt danach der Foto-Upload fehl, aktualisiert ein erneutes Speichern dieses Mitglied statt ein zweites anzulegen
      editingMember.value = created
    }
    if (pendingPhoto.value) {
      await adminService.uploadMemberPhoto(memberId, pendingPhoto.value)
    } else if (photoRemoved.value) {
      await adminService.deleteMemberPhoto(memberId)
    }
    closeModal()
    await loadMembers()
  } catch (err: any) {
    formError.value = err.response?.data?.error || 'Fehler beim Speichern des Mitglieds'
  }
}

const confirmDelete = async (member: AdminMember) => {
  if (confirm(`Möchten Sie "${member.firstName} ${member.lastName}" wirklich löschen?`)) {
    try {
      await adminService.deleteMember(member.id)
      await loadMembers()
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Fehler beim Löschen des Mitglieds'
    }
  }
}

onMounted(() => {
  loadMembers()
})
</script>

